/* ==========================================================================
   MONTAGE-THEME · <mt-sticky-atc>
   --------------------------------------------------------------------------
   Die Kaufleiste am unteren Rand. Zwei Aufgaben:

   1. Sichtbar werden, sobald der eigentliche Kaufblock aus dem Bild ist,
      und wieder verschwinden, sobald er zurueckkommt. Sonst stehen zwei
      Kaufbuttons gleichzeitig auf dem Schirm und teilen den Klick.

   2. Immer die Variante fuehren, die oben ausgewaehlt ist. Deshalb liest
      die Leiste das versteckte id-Feld des Hauptformulars mit, statt die
      Variantenlogik ein zweites Mal zu bauen. Was der Kunde oben waehlt,
      liegt unten im Warenkorb.

   Bewusst per DOM gekoppelt und nicht ueber Dawn-Interna: ein
   MutationObserver auf dem id-Feld ueberlebt ein Theme-Update, ein
   Zugriff auf interne Ereignisnamen nicht.
   ========================================================================== */
/* Gekapselt in einen sofort ausgefuehrten Ausdruck, weil mehrere Sektionen
   dieselbe Datei einbinden koennen. Zwei script-Tags mit derselben Adresse
   fuehrt der Browser zweimal aus, und eine Klasse im Modulrumpf wirft beim
   zweiten Mal "has already been declared". Innerhalb der Kapsel ist die
   zweite Ausfuehrung folgenlos, die Registrierungspruefung darunter faengt
   den Rest ab. */
(function () {
  class MontageStickyAtc extends HTMLElement {
    connectedCallback() {
      this.ziel = document.querySelector(this.dataset.beobachte || '');
      this.eigenesFeld = this.querySelector('input[name="id"]');
      this.knopf = this.querySelector('button[type="submit"]');

      this.beobachteSichtbarkeit();
      this.folgeVariante();
    }

    disconnectedCallback() {
      this.sichtWaechter?.disconnect();
      this.feldWaechter?.disconnect();
      this.knopfWaechter?.disconnect();
      if (this.scrollWaechter) window.removeEventListener('scroll', this.scrollWaechter);
    }

    /* ── 1 · Ein- und ausblenden ────────────────────────────────────────── */
    beobachteSichtbarkeit() {
      if (!this.ziel) {
        // Ohne Bezugspunkt bleibt die Leiste dauerhaft stehen, statt zu fehlen.
        this.setAttribute('data-sichtbar', '');
        return;
      }
      /* 🔴 Bezugspunkt ist der KAUFKNOPF, nicht der ganze Infoblock.
         Der Block ist am Telefon rund 1500 px hoch. Wer ihn als Ganzes
         beobachtet, schaltet entweder zu frueh oder erst weit hinter dem
         Kaufbereich. Der Knopf ist die Stelle, ab der die Leiste einen
         Zweck hat: sie ersetzt genau ihn, sobald er weg ist. */
      /* Die Kaufzeile geht dem Knopf VOR, wo es sie gibt.
         Sie fasst beide Knoepfe: den Kaufknopf und den fuer die
         Benachrichtigung bei ausverkaufter Variante. Genau einer von beiden
         ist sichtbar, der andere ist per CSS ausgeblendet. Ein ausgeblendetes
         Element hat keine Flaeche, der Beobachter wuerde daran nie ausloesen
         und die Leiste erschiene nie. Die Zeile ist ebenso kompakt wie der
         Knopf allein, nur um die beiden Zusagezeilen hoeher, und sie bleibt
         in jedem Zustand sichtbar. */
      this.ziel =
        this.ziel.querySelector('.mt-cta') ||
        this.ziel.querySelector('product-form button[name="add"], .product-form__submit') ||
        this.ziel;

      /* 🔴 Die Leiste erscheint erst, wenn das Ziel nach OBEN aus dem Bild
         gewandert ist.

         Vorher stand hier nur `!eintrag.isIntersecting` mit einem rootMargin
         von -80 %. Damit galt schon der Seitenanfang als "nicht sichtbar":
         der Kaufbereich liegt beim Aufruf weit unterhalb des obersten
         Fuenftels, und die Leiste stand vom ersten Moment an da. Sie bot
         damit etwas an, das zwei Fingerbreit darunter ohnehin sichtbar war,
         und verdeckte dabei den unteren Rand der Seite.

         Die zweite Bedingung unterscheidet die beiden Richtungen: unterhalb
         des Bildes (noch nicht hingescrollt) bleibt die Leiste weg, oberhalb
         (vorbeigescrollt) kommt sie. */
      /* Nachschaerfung 13.09.2026 (Moritz: „etwas mehr sticky"): die Leiste
         kommt auch, wenn der Kaufknopf noch UNTER dem Bild liegt, sobald
         der Kunde ein Stueck gescrollt hat. Am Telefon liegt der Knopf
         hinter Titel, Kaufgruenden und den drei Stufenkarten, also rund
         800 px tief: wer dort liest, hatte bis jetzt keinen Kaufknopf im
         Bild. Der Seitenanfang bleibt frei (erste 240 px), damit die Leiste
         nicht schon beim Aufruf ueber dem Hero liegt, das war der Fehler
         der Fassung vor dem 04.09. Sichtbar ist der Knopf selbst, geht die
         Leiste weg, sonst stehen zwei Kaufknoepfe im Bild. */
      const SCHWELLE = 240;
      let imBild = true;
      const entscheiden = () => {
        const vorbei = !imBild && this.ziel.getBoundingClientRect().bottom <= 0;
        const darunterUndGescrollt = !imBild && window.scrollY > SCHWELLE;
        this.toggleAttribute('data-sichtbar', vorbei || darunterUndGescrollt);
      };
      this.sichtWaechter = new IntersectionObserver(
        ([eintrag]) => {
          imBild = eintrag.isIntersecting;
          entscheiden();
        },
        { threshold: 0 }
      );
      this.sichtWaechter.observe(this.ziel);
      // Die Schwelle haengt am Scrollweg, den der Beobachter nicht meldet.
      // Passiv, damit das Scrollen nicht auf den Handler wartet.
      this.scrollWaechter = () => entscheiden();
      window.addEventListener('scroll', this.scrollWaechter, { passive: true });
    }

    /* ── 2 · Der oben gewaehlten Variante folgen ────────────────────────── */
    folgeVariante() {
      if (!this.eigenesFeld) return;
      // 🔴 Der Bezug MUSS auf den Kaufblock eingegrenzt werden.
      // Fruehere Fassung suchte dokumentweit nach dem ersten
      // form[action*="/cart/add"]. Die Warenkorb-Schublade steht in
      // theme.liquid VOR <main> und traegt selbst so ein Formular je
      // Upsell-Vorschlag. querySelector liefert den ersten Treffer in
      // DOKUMENTREIHENFOLGE, nicht den ersten passenden Selektor: die Leiste
      // uebernahm damit die Variante des Upsells. Am Live-Theme belegt, die
      // Selfpress-Seite bot ueber die Leiste den Squeeze-Wechselfilter an.
      const bereich =
        this.ziel?.closest('.shopify-section') ||
        document.querySelector('.product__info-wrapper')?.closest('.shopify-section');
      const hauptform =
        bereich?.querySelector('product-form form[action*="/cart/add"]') ||
        // Notnagel, falls der Kaufblock anders heisst: irgendein Kauf-Formular,
        // aber niemals eines aus der Schublade und niemals das eigene.
        [...document.querySelectorAll('product-form form[action*="/cart/add"]')].find(
          (f) => !f.closest('cart-drawer') && !f.closest('mt-sticky-atc')
        );
      this.hauptfeld = hauptform?.querySelector('input[name="id"]');
      if (!this.hauptfeld) return;
      // Der Ausverkauft-Zustand haengt am KNOPF, nicht am Feld. Dawn sperrt
      // beim Variantenwechsel button[name="add"] und tauscht seine Beschriftung,
      // das versteckte id-Feld bleibt dabei unberuehrt. Wer nur das Feld
      // beobachtet, laesst die Leiste "In den Warenkorb" anbieten, waehrend
      // oben laengst "Ausverkauft" steht. Am Live-Theme belegt.
      this.hauptknopf = hauptform?.querySelector('button[name="add"], [type="submit"][name="add"]');

      this.uebernimm();
      // Dawn tauscht bei Variantenwechsel den Wert des Feldes aus.
      this.feldWaechter = new MutationObserver(() => this.uebernimm());
      this.feldWaechter.observe(this.hauptfeld, { attributes: true, attributeFilter: ['value', 'disabled'] });
      if (this.hauptknopf) {
        this.knopfWaechter = new MutationObserver(() => this.uebernimm());
        this.knopfWaechter.observe(this.hauptknopf, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      }
      this.hauptfeld.addEventListener('change', () => this.uebernimm());
    }

    uebernimm() {
      const wert = this.hauptfeld.value;
      if (wert) {
        this.eigenesFeld.value = wert;
        // Ein gesetzter .value loest von sich aus nichts aus. mt-regulaer.js
        // haengt am change-Ereignis dieses Feldes, damit der Streichpreis in
        // der Leiste der Variante folgt (13.09.2026).
        this.eigenesFeld.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /*
       * Ein deaktiviertes id-Feld heisst NICHT mehr zwangslaeufig ausverkauft.
       * Der Stufen-Kaufblock schaltet es ab, weil Shopifys Warenkorb-Route
       * entweder `id` ODER `items[]` nimmt, nie beides. Er markiert das Feld
       * dabei mit `data-mt-stufen`, damit hier beide Faelle unterscheidbar
       * bleiben. Ohne diese Ausnahme stand die Leiste dauerhaft auf
       * "Ausverkauft", waehrend der Kaufblock daneben normal kaufbar war
       * (live aufgetreten 04.09.2026).
       */
      const feldAusDurchStufen = this.hauptfeld.dataset.mtStufen !== undefined;
      const ausverkauft =
        (this.hauptfeld.disabled && !feldAusDurchStufen) ||
        this.hauptknopf?.disabled ||
        this.hauptknopf?.getAttribute('aria-disabled') === 'true';
      if (this.knopf) {
        this.knopf.disabled = !!ausverkauft;
        // Zustaende werden beschriftet, nicht nur ausgegraut.
        //
        // 🔴 Die Beschriftung geht in das <span> im Knopf, NICHT auf den
        // Knopf selbst. Seit der Kauf ueber Dawns <product-form> laeuft,
        // stehen dort zwei Kinder: das span mit dem Text und der
        // Ladekringel. Ein textContent auf dem Knopf wuerfe beide weg,
        // und product-form.js greift beim Absenden direkt auf sie zu
        // (submitButtonText, .loading__spinner). Der erste Klick nach
        // einem Variantenwechsel liefe damit auf einen Fehler.
        const beschriftung = this.knopf.querySelector('span') || this.knopf;
        beschriftung.textContent = ausverkauft
          ? this.dataset.textAusverkauft || 'Sold out'
          : this.dataset.textKaufen || 'Add to cart';
      }
    }
  }

  if (!customElements.get('mt-sticky-atc')) {
    customElements.define('mt-sticky-atc', MontageStickyAtc);
  }
})();
